import { ReactElement, SyntheticEvent, useMemo, useCallback } from 'react';

import currentTheme from '~config/theme';
import { toAbsoluteUrl } from '~utils/assetsHelpers';
import { COLLECT_STATE_OPTIONS } from '~constants/ordersStatesOptions';

import { useDashboardVariantV2CarrierContext } from '../../../Context';
import { useDashboardVariantV2CarrierCollectFiltersContext } from '../../../CollectFiltersContext';
import { useDashboardVariantV2CarrierPresetsContext } from '../../../PresetsContext';
import { DashboardVariantV2CarrierContextTabId } from '../../../types';
import {
  DashboardVariantV2CarrierStatusInfoWidgetCard,
  DashboardVariantV2CarrierStatusItemsList,
  DashboardVariantV2CarrierHeaderCaptionText,
} from '../../SubComponents';

const WIDGET_TAB_ID: DashboardVariantV2CarrierContextTabId = 'collect';
const WIDGET_ACCENT_COLOR = currentTheme.palette.subscribe.main;

const [TO_COLLECT_STATE_OPTION, COLLECT_STATE_OPTION] = COLLECT_STATE_OPTIONS;

const DashboardVariantV2CarrierCollectWidgetCard = (): ReactElement => {
  const {
    statistics: {
      data: {
        totalItems,
        collect: {
          totalCollectItems,
          totalToCollectItems,
          totalCollectedItems,
        },
      },
    },
    activeTab: { getIsActiveTab, setActiveTab },
  } = useDashboardVariantV2CarrierContext();

  const {
    autocompletesRefs: { collectState: collectStateAutocompleteRef },
    filters: { handleClearFilters, handleClearFilterByValues },
    forceRequest: { toggleValue: toggleForceRequest },
  } = useDashboardVariantV2CarrierCollectFiltersContext();

  const {
    selectedPreset: { reset: handleResetSelectedPreset },
  } = useDashboardVariantV2CarrierPresetsContext();

  const isSelected = useMemo(
    () => getIsActiveTab(WIDGET_TAB_ID),
    [getIsActiveTab],
  );

  const handleSelectedTab = useCallback(() => {
    if (!isSelected) {
      handleResetSelectedPreset();
    }

    setActiveTab(WIDGET_TAB_ID);
  }, [isSelected, setActiveTab, handleResetSelectedPreset]);

  const handleSelectedTabByFilters = useCallback(
    (values: Record<string, unknown>, callback?: () => void) =>
      (event: SyntheticEvent) => {
        event.stopPropagation();

        handleClearFilters(false);
        handleClearFilterByValues(values, () => toggleForceRequest(isSelected));
        handleResetSelectedPreset();

        setActiveTab(WIDGET_TAB_ID);

        if (callback) {
          setTimeout(() => {
            callback();
          }, 150);
        }
      },
    [
      handleClearFilterByValues,
      handleResetSelectedPreset,
      isSelected,
      setActiveTab,
      toggleForceRequest,
      handleClearFilters,
    ],
  );

  const handleSelectedTabWithToCollectState = useCallback(
    (event: SyntheticEvent) => {
      handleSelectedTabByFilters(
        { collectState: [TO_COLLECT_STATE_OPTION] },
        () =>
          collectStateAutocompleteRef.current?.setValue([
            TO_COLLECT_STATE_OPTION,
          ]),
      )(event);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleSelectedTabByFilters],
  );

  const handleSelectedTabWithCollectState = useCallback(
    (event: SyntheticEvent) => {
      handleSelectedTabByFilters({ collectState: [COLLECT_STATE_OPTION] }, () =>
        collectStateAutocompleteRef.current?.setValue([COLLECT_STATE_OPTION]),
      )(event);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleSelectedTabByFilters],
  );

  return (
    <DashboardVariantV2CarrierStatusInfoWidgetCard
      accentColor={WIDGET_ACCENT_COLOR}
      isSelected={isSelected}
      onSelected={handleSelectedTab}
      value={{
        current: totalCollectItems,
        total: totalItems,
      }}
      title={{
        text: 'COLECTA',
        tooltip:
          // eslint-disable-next-line max-len
          'Pedidos que se encuentran en el circuito de colecta desde dadores de carga hacia el depósito.',
      }}
      icon={{
        src: toAbsoluteUrl('/icons/icon_section_collect.svg'),
        alt: 'Section Collect Icon',
      }}
      content={
        <DashboardVariantV2CarrierStatusItemsList
          items={[
            {
              label: 'A colectar',
              value: totalToCollectItems,
              onClick: handleSelectedTabWithToCollectState,
            },
            {
              label: 'Colectados',
              value: totalCollectedItems,
              onClick: handleSelectedTabWithCollectState,
            },
          ]}
        />
      }
      footer={
        <DashboardVariantV2CarrierHeaderCaptionText color={WIDGET_ACCENT_COLOR}>
          Ver colecta de pedidos
        </DashboardVariantV2CarrierHeaderCaptionText>
      }
    />
  );
};

export default DashboardVariantV2CarrierCollectWidgetCard;
